/**
 * The one AudioContext the console plays through, and whether the
 * browser is letting it make a sound.
 *
 * WHY ONE. The chime (`alert-audio.ts`) and the fire siren
 * (`fire-alarm.siren.default.ts`) each made their own context lazily,
 * from inside the first chime or siren: never from a tap. A context
 * made outside a user gesture starts `suspended` on iOS (and in Chrome
 * until the page has been interacted with), and `resume()` called from
 * there is refused. So the desk iPad could stay silent through a fire
 * alarm, siren included, until somebody happened to tap the page
 * (Decision 18; twin-foh-ipad-setup.md). With one context, one tap
 * unlocks both, and the "Tap to enable alert sound" chip
 * (`SoundUnlockChip`) has one state to read.
 *
 * ANY TAP UNLOCKS IT, not only a tap on the chip: a listener on the
 * document resumes a suspended context on the first click, touch or key
 * press anywhere, because staff use that iPad for other things and each
 * of those taps is as good a gesture as the chip's. The chip is the
 * prompt for when nobody has touched the page yet. The listener stays
 * for the life of the page: iOS can suspend the context again later,
 * and the next tap should bring it back.
 */
type Listener = () => void;

let ctx: AudioContext | null = null;
const listeners = new Set<Listener>();
let gesturesInstalled = false;

function notify(): void {
  for (const l of listeners) l();
}

function contextClass(): typeof AudioContext | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    AudioContext?: typeof AudioContext;
    webkitAudioContext?: typeof AudioContext;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

/** The gestures a browser accepts as permission to start audio. iOS
 *  honours `touchend` and `click`, not `touchstart`. */
const UNLOCK_GESTURES = ['pointerup', 'touchend', 'click', 'keydown'] as const;

function installGestureUnlock(): void {
  if (gesturesInstalled || typeof document === 'undefined') return;
  gesturesInstalled = true;
  const onGesture = () => {
    // Synchronously, inside the handler: that is the only place the
    // browser honours it.
    if (ctx && ctx.state === 'suspended') void ctx.resume().catch(() => {});
  };
  for (const type of UNLOCK_GESTURES) {
    document.addEventListener(type, onGesture, { capture: true, passive: true });
  }
}

/** The shared context, made on first use. Null where there is no Web
 *  Audio at all, or the browser refuses to make one. */
export function sharedAudioContext(): AudioContext | null {
  if (ctx) return ctx;
  const Ctx = contextClass();
  if (!Ctx) return null;
  try {
    ctx = new Ctx();
  } catch {
    return null;
  }
  // `statechange` is what tells the chip a tap anywhere worked.
  ctx.addEventListener?.('statechange', notify);
  installGestureUnlock();
  notify();
  return ctx;
}

/**
 * - `unavailable`: no context (no Web Audio, or not made yet).
 * - `locked`: the browser is holding it silent until a tap.
 * - `ready`: it can play. Safari's `interrupted` (a call, the screen
 *   locking) counts as ready: it comes back by itself, and a "tap to
 *   enable" chip would be the wrong thing to show for it.
 */
export type AudioUnlockState = 'unavailable' | 'locked' | 'ready';

export function audioUnlockState(): AudioUnlockState {
  if (!ctx || ctx.state === 'closed') return 'unavailable';
  return ctx.state === 'suspended' ? 'locked' : 'ready';
}

export function subscribeAudioUnlock(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Resume the shared context. Call it from inside a tap or key handler:
 * `resume()` is started synchronously, which is the only place a
 * browser honours it. Resolves true once the context is running.
 */
export function unlockAudio(): Promise<boolean> {
  const c = sharedAudioContext();
  if (!c) return Promise.resolve(false);
  if (c.state !== 'suspended') return Promise.resolve(c.state === 'running');
  return c.resume().then(
    () => {
      notify();
      return c.state === 'running';
    },
    () => false,
  );
}

/** Forget the context between tests; never called by the app. */
export function __resetAudioContextForTests(): void {
  ctx = null;
  listeners.clear();
}
